import OpenAI from 'openai';
import { Message as AppMessage } from './types'; // Import our app's Message type
import { modelHasCapability } from './models'; // Import modelHasCapability

// Initialize the OpenAI client using the environment variable
const openai = new OpenAI({
    apiKey: process.env.REACT_APP_OPENAI_API_KEY,
    dangerouslyAllowBrowser: true,
});

/**
 * Converts our app's message format to OpenAI's message format.
 * 
 * @param messages Array of messages in our app's format
 * @returns An array of formatted messages for OpenAI API
 */
const processMessagesForOpenAI = (messages: any[]): any[] => {
    return messages.map(msg => {
        let role = msg.role;
        let content = msg.content;

        if (typeof content !== 'string') {
            content = JSON.stringify(content);
        }

        return { role, content };
    });
};

/**
 * Sends a message to the specified OpenAI model and returns a stream.
 *
 * @param model The ID of the OpenAI model to use (e.g., 'gpt-4o').
 * @param messages An array of message objects representing the conversation history.
 * @param maxTokens The maximum number of tokens to generate.
 * @returns A stream of completion chunk events from the OpenAI API.
 */
export const streamOpenAIMessage = async (
    model: string,
    messages: any[],
    maxTokens: number = 1024
): Promise<any> => {
    try {
        const formattedMessages = processMessagesForOpenAI(messages);

        // Check if the model is an O4 model (which requires max_completion_tokens)
        const isO4Model = model.toLowerCase().includes('o4-') || model.toLowerCase() === 'o4';

        // Create the request options based on the model type
        const requestOptions: any = {
            model: model,
            messages: formattedMessages,
            stream: true
        };

        // Use the correct token parameter based on the model
        if (isO4Model) {
            requestOptions.max_completion_tokens = maxTokens;
        } else {
            requestOptions.max_tokens = maxTokens;
        }

        // The stream returned by OpenAI SDK is already properly iterable
        return await openai.chat.completions.create(requestOptions);
    } catch (error) {
        console.error('Error calling OpenAI API stream:', error);
        throw error;
    }
};

/**
 * Creates a completion using OpenAI's API.
 *
 * @param model The ID of the OpenAI model to use.
 * @param input The text input to generate a completion for.
 * @param maxTokens The maximum number of tokens to generate.
 * @returns A stream of completion events.
 */
export const streamOpenAIResponse = async (
    model: string,
    input: string,
    maxTokens: number = 1024
): Promise<any> => {
    try {
        return await openai.completions.create({
            model: model,
            prompt: input,
            max_tokens: maxTokens,
            stream: true
        });
    } catch (error) {
        console.error('Error calling OpenAI API stream:', error);
        throw error;
    }
}; 